import { API_CONFIG } from '../config/api';
import { secureTokenManager } from '../utils/secureTokenManager';

// Payment History interfaces based on the API response
export interface PaymentHistoryServiceProvider {
  id: string;
  fullName: string;
  contactNumber: string;
  email: string;
  serviceProviderType: string;
  status: string;
  isActive: boolean;
  registeredOn: string;
}

export interface PaymentHistoryPayment {
  status: 'completed' | 'pending' | 'failed';
  transactionId: string;
  amount: number;
  paymentMethod: string;
  membershipType: string;
  paymentDate: string;
  paymentDateRaw: string;
}

export interface PaymentHistoryItem {
  id: string;
  serviceProvider: PaymentHistoryServiceProvider;
  payment: PaymentHistoryPayment;
}

export interface PaymentHistorySummary {
  totalPayments: number;
  totalAmount: number;
  statusCounts: {
    completed: number;
    pending: number;
    failed: number;
  };
}

export interface PaymentHistoryPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PaymentHistoryFilters {
  paymentStatus?: 'completed' | 'pending' | 'failed';
  serviceProviderType?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export interface PaymentHistoryResponse {
  success: boolean;
  data: {
    paymentHistory: PaymentHistoryItem[];
    summary: PaymentHistorySummary;
    pagination: PaymentHistoryPagination;
    filters: {
      paymentStatus: string | null;
      serviceProviderType: string | null;
      search: string | null;
      startDate: string | null;
      endDate: string | null;
    };
  };
}

export interface PaymentHistoryFilters {
  page?: number;
  limit?: number;
  paymentStatus?: 'completed' | 'pending' | 'failed';
  serviceProviderType?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export class PaymentHistoryService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authToken = secureTokenManager.getAdminSessionToken();

    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    // Debug logging removed for security

    try {
      const response = await fetch(url, config);
      
      // Response logging removed for security
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Payment History API Error:', errorData);
        throw new Error(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const data = await response.json();
      // Success logging removed for security
      return data;
    } catch (error) {
      console.error('💥 Payment History API Request Failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Get payment history with filters
  async getPaymentHistory(filters: PaymentHistoryFilters = {}): Promise<PaymentHistoryResponse> {
    const params = new URLSearchParams();
    
    // Add pagination parameters
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    
    // Add filtering parameters
    if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters.serviceProviderType) params.append('serviceProviderType', filters.serviceProviderType);
    if (filters.search) params.append('search', filters.search);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);

    const queryString = params.toString();
    const endpoint = `/api/admin/payment-history${queryString ? `?${queryString}` : ''}`;
    
    // Endpoint logging removed for security
    
    return this.makeRequest<PaymentHistoryResponse>(endpoint);
  }

  // Test API connection
  async testConnection(): Promise<boolean> {
    try {
      // Connection test logging removed for security
      await this.makeRequest<PaymentHistoryResponse>('/api/admin/payment-history?page=1&limit=1');
      return true;
    } catch (error) {
      console.error('❌ Payment History API connection failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const paymentHistoryService = new PaymentHistoryService();

// Make test method available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testPaymentHistoryAuth = () => {
    // Authentication test logging removed for security
    return paymentHistoryService.testConnection();
  };
  
  (window as any).paymentHistoryService = paymentHistoryService;
}
